import { jsPDF } from "jspdf";
import { AppInventoryItem } from "@/types/inventory";
import { AggregatedAppCveResult, isAppleApp } from "@/services/appCveMatcher";

export interface AppCveExportRow {
  app: AppInventoryItem;
  match?: AggregatedAppCveResult;
}

export interface ExportSummary {
  vulnerable: number;
  critical: number;
  high: number;
  totalApps: number;
}

const SMPLIFY_LOGO_URL = "/lovable-uploads/smplify-logo-symbol.png";
const SMPLIFY_BRAND = "Smplify · Partner Portal";
// Smplify primary brand color (HSL 222.2 47.4% 11.2%) → RGB
const BRAND_RGB: [number, number, number] = [15, 23, 42];

function statusFor(row: AppCveExportRow): string {
  if (isAppleApp(row.app.bundle_id)) return "Tracked via OS feed";
  const m = row.match;
  if (!m || m.cves.length === 0) return "Clean";
  return `${m.highestSeverity} (${m.cves.length})`;
}

function vulnerableDeviceCount(row: AppCveExportRow): number {
  if (isAppleApp(row.app.bundle_id)) return 0;
  const m = row.match;
  if (!m || m.cves.length === 0) return 0;
  if (m.vulnerableVersions.length === 0) return row.app.total_devices;
  const vuln = new Set(m.vulnerableVersions);
  return row.app.versions
    .filter((v) => vuln.has(v.version))
    .reduce((sum, v) => sum + (v.device_count ?? 0), 0);
}

function allVersions(row: AppCveExportRow): string[] {
  return row.app.versions.map((v) => v.version).filter(Boolean);
}

function vulnerableVersions(row: AppCveExportRow): string[] {
  return row.match?.vulnerableVersions ?? [];
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function buildAppCveCsv(rows: AppCveExportRow[]): string {
  const header = [
    "Application",
    "Bundle ID",
    "Versions",
    "Vulnerable Versions",
    "Devices",
    "Vulnerable Devices",
    "CVE Status",
    "CVE IDs",
  ];
  const lines = [header.join(",")];
  for (const row of rows) {
    const cveIds = row.match?.cves.map((c) => c.cveId).join("; ") ?? "";
    lines.push(
      [
        csvEscape(row.app.name),
        csvEscape(row.app.bundle_id),
        csvEscape(allVersions(row).join("; ")),
        csvEscape(vulnerableVersions(row).join("; ")),
        String(row.app.total_devices),
        String(vulnerableDeviceCount(row)),
        csvEscape(statusFor(row)),
        csvEscape(cveIds),
      ].join(","),
    );
  }
  return lines.join("\n");
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, filename);
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function loadLogoDataUrl(): Promise<string | null> {
  try {
    const res = await fetch(SMPLIFY_LOGO_URL);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function generateAppCvePdf(
  rows: AppCveExportRow[],
  summary: ExportSummary,
): Promise<Blob> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  const logoDataUrl = await loadLogoDataUrl();

  const drawBrandedHeader = () => {
    // Brand bar
    doc.setFillColor(...BRAND_RGB);
    doc.rect(0, 0, pageWidth, 56, "F");
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, "PNG", margin, 14, 28, 28);
      } catch {
        // ignore — fall back to text-only
      }
    }
    doc.setTextColor(255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text(SMPLIFY_BRAND, logoDataUrl ? margin + 38 : margin, 32);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Application CVE Report", logoDataUrl ? margin + 38 : margin, 46);
    doc.setTextColor(0);
  };

  const drawFooter = (pageNum: number, pageCount: number) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(SMPLIFY_BRAND, margin, pageHeight - 18);
    doc.text(
      `Page ${pageNum} of ${pageCount}`,
      pageWidth - margin,
      pageHeight - 18,
      { align: "right" },
    );
    doc.setTextColor(0);
  };

  drawBrandedHeader();
  let y = 80;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
  y += 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Summary", margin, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const totalVulnerableDevices = rows.reduce((sum, r) => sum + vulnerableDeviceCount(r), 0);
  const summaryLines = [
    `Total apps in view: ${summary.totalApps}`,
    `Vulnerable apps: ${summary.vulnerable}`,
    `Vulnerable device installs: ${totalVulnerableDevices}`,
    `Critical CVEs (highest sev): ${summary.critical}`,
    `High CVEs (highest sev): ${summary.high}`,
  ];
  for (const line of summaryLines) {
    doc.text(line, margin, y);
    y += 14;
  }
  y += 8;

  // Table columns sized for A4 portrait (~515pt content width)
  const cols = [
    { label: "Application", width: 110, key: "name" as const },
    { label: "Bundle ID", width: 130, key: "bundle" as const },
    { label: "Versions", width: 90, key: "versions" as const },
    { label: "Vuln Versions", width: 90, key: "vulnVersions" as const },
    { label: "Dev", width: 30, key: "devices" as const },
    { label: "Vuln Dev", width: 40, key: "vulnDev" as const },
    { label: "Status", width: 70, key: "status" as const },
  ];

  const drawTableHeader = () => {
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y - 10, pageWidth - margin * 2, 16, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    let x = margin + 4;
    for (const col of cols) {
      doc.text(col.label, x, y);
      x += col.width;
    }
    y += 12;
    doc.setFont("helvetica", "normal");
  };
  drawTableHeader();

  const truncate = (text: string, max: number) =>
    text.length > max ? text.slice(0, max - 1) + "…" : text;

  const charWidthFor = (width: number) => Math.max(6, Math.floor(width / 4.2));

  doc.setFontSize(8.5);
  for (const row of rows) {
    if (y > pageHeight - margin - 30) {
      doc.addPage();
      drawBrandedHeader();
      y = 80;
      drawTableHeader();
      doc.setFontSize(8.5);
    }
    const versions = allVersions(row);
    const vulnV = vulnerableVersions(row);
    const values: Record<typeof cols[number]["key"], string> = {
      name: row.app.name,
      bundle: row.app.bundle_id,
      versions: versions.length ? versions.join(", ") : "—",
      vulnVersions: vulnV.length ? vulnV.join(", ") : "—",
      devices: String(row.app.total_devices),
      vulnDev: String(vulnerableDeviceCount(row)),
      status: statusFor(row),
    };
    let x = margin + 4;
    for (const col of cols) {
      doc.text(truncate(values[col.key], charWidthFor(col.width)), x, y);
      x += col.width;
    }
    y += 13;
  }

  // Footer pagination
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawFooter(i, total);
  }

  return doc.output("blob");
}

export function downloadAppCvePdf(filename: string, blob: Blob) {
  triggerDownload(blob, filename);
}
